const ENGLISH_ONLY_ROUTE_PATHS = new Set([
  '/',
  '/home/',
  '/download/',
  '/dock/',
  '/pricing/',
  '/features/',
  '/features/ai-assistant/',
  '/features/cross-platforms/',
  '/features/overview/',
  '/features/sync/',
  '/features/tree-structure/',
])

export type LocaleCode = 'zh-cn' | 'en-us'

const LOCALIZED_ROUTE_PAIRS = [
  { en: '/', zh: '/home-zh/' },
  { en: '/home/', zh: '/home-zh/' },
  { en: '/download/', zh: '/download-zh/' },
  { en: '/dock/', zh: '/dock-zh/' },
  { en: '/pricing/', zh: '/pricing-zh/' },
  { en: '/features/', zh: '/features-zh/' },
  { en: '/about/', zh: '/about-zh/' },
  { en: '/privacy/', zh: '/privacy-zh/' },
  { en: '/terms/', zh: '/terms-zh/' },
  { en: '/features/goal/', zh: '/features/goal-zh/' },
  { en: '/features/habits/', zh: '/features/habits-zh/' },
  { en: '/features/notes/', zh: '/features/notes-zh/' },
  { en: '/features/planner/', zh: '/features/planner-zh/' },
  { en: '/features/ai-assistant/', zh: '/features/ai-assistant-zh/' },
  { en: '/features/cross-platforms/', zh: '/features/cross-platforms-zh/' },
  { en: '/features/overview/', zh: '/features/overview-zh/' },
  { en: '/features/sync/', zh: '/features/sync-zh/' },
  { en: '/features/tree-structure/', zh: '/features/tree-structure-zh/' },
] as const

const normalizePathname = (pathname?: string) => {
  if (!pathname) {
    return undefined
  }

  const normalized = pathname.replace(/^\/+|\/+$/g, '')

  return normalized ? `/${normalized}/` : '/'
}

const normalizeSearch = (search?: string) => {
  if (!search) {
    return ''
  }

  return search.startsWith('?') ? search : `?${search}`
}

const normalizeHash = (hash?: string) => {
  if (!hash) {
    return ''
  }

  return hash.startsWith('#') ? hash : `#${hash}`
}

const getLocaleFromSearch = (search?: string) => {
  if (!search || typeof URLSearchParams === 'undefined') {
    return undefined
  }

  const lang = new URLSearchParams(normalizeSearch(search).replace(/^\?/, '')).get('lang')?.toLowerCase()

  if (lang === 'zh-cn' || lang === 'en-us') {
    return lang
  }

  return undefined
}

const getLocaleFromPathname = (pathname?: string) => {
  const normalizedPathname = normalizePathname(pathname)

  if (!normalizedPathname) {
    return undefined
  }

  if (normalizedPathname.includes('-zh/')) {
    return 'zh-CN'
  }

  if (ENGLISH_ONLY_ROUTE_PATHS.has(normalizedPathname)) {
    return 'en'
  }

  return undefined
}

export const hasLocalizedRoutePair = (pathname?: string) => {
  const normalizedPathname = normalizePathname(pathname)

  if (!normalizedPathname) {
    return false
  }

  return LOCALIZED_ROUTE_PAIRS.some(
    ({ en, zh }) => normalizedPathname === en || normalizedPathname === zh,
  )
}

const getLocalizedRoutePair = (pathname?: string) => {
  const normalizedPathname = normalizePathname(pathname)

  if (!normalizedPathname) {
    return undefined
  }

  return LOCALIZED_ROUTE_PAIRS.find(
    ({ en, zh }) => normalizedPathname === en || normalizedPathname === zh,
  )
}

export const getLocalizedPath = (
  path: string,
  pathnameOrIsZh?: string | boolean,
) => {
  const normalizedPath = normalizePathname(path)

  if (!normalizedPath) {
    return path
  }

  const isZhLang = typeof pathnameOrIsZh === 'boolean'
    ? pathnameOrIsZh
    : isZhCN(pathnameOrIsZh)

  const routePair = LOCALIZED_ROUTE_PAIRS.find(
    ({ en, zh }) => normalizedPath === en || normalizedPath === zh,
  )

  if (!routePair) {
    return path
  }

  return isZhLang ? routePair.zh : routePair.en
}

export const getLanguageSwitchPath = ({
  pathname,
  search,
  hash,
  targetLocale,
}: {
  pathname?: string
  search?: string
  hash?: string
  targetLocale: LocaleCode
}) => {
  const fallbackPath = pathname || '/'
  const normalizedPathname = normalizePathname(fallbackPath) ?? fallbackPath
  const targetIsZh = targetLocale === 'zh-cn'
  const localizedPath = getLocalizedPath(normalizedPathname, targetIsZh)
  const hasDedicatedLocalizedRoute = Boolean(getLocalizedRoutePair(normalizedPathname))
  const params = new URLSearchParams(normalizeSearch(search).replace(/^\?/, ''))

  if (hasDedicatedLocalizedRoute) {
    params.delete('lang')
    const nextSearch = params.toString()

    return `${localizedPath}${nextSearch ? `?${nextSearch}` : ''}${normalizeHash(hash)}`
  }

  params.set('lang', targetLocale)

  return `${fallbackPath}${params.toString() ? `?${params.toString()}` : ''}${normalizeHash(hash)}`
}

export const isZhCNByLocation = (pathname?: string, search?: string) => {
  const localeFromPathname = getLocaleFromPathname(pathname)

  if (localeFromPathname) {
    return localeFromPathname === 'zh-CN'
  }

  const localeFromSearch = getLocaleFromSearch(search)

  if (localeFromSearch && !pathname) {
    return localeFromSearch === 'zh-cn'
  }

  if (pathname) {
    return false
  }

  if (localeFromSearch) {
    return localeFromSearch === 'zh-cn'
  }

  if (typeof window !== 'undefined') {
    return window.isZhCN()
  }

  return false
}

export const isZhCNByRoute = (pathname?: string) => {
  const localeFromPathname = getLocaleFromPathname(pathname)

  return localeFromPathname === 'zh-CN'
}

export const isZhCN = (pathname?: string) => {
  if (pathname) {
    return isZhCNByRoute(pathname)
  }

  return isZhCNByLocation(
    pathname ?? (typeof window !== 'undefined' ? window.location.pathname : undefined),
    typeof window !== 'undefined' ? window.location.search : undefined,
  )
}

export const getZhCnTranscript = (
  localeIsZhCN: string,
  localeNonZhCN: string,
  pathname?: string,
) => (isZhCN(pathname) ? localeIsZhCN : localeNonZhCN)
